import React, { useState } from 'react';
import { Tooltip, Box, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ContentHeaderField from '../../../../components/shared/ContentHeaderField';
import { CONTENT_HEADER_FIELD_DEFS } from '../../../../components/shared/contentHeaderFieldDefs';
import { tooltipSxElevated } from '../../../../components/shared/jernyTooltipStyling';
import topicGroupSearchEngine from './TopicGroupSearchEngine';
import './TopicGroupSearchDialog.css';

const PIN_FEEDBACK_MS = 3000;

// Chip keys shown in the result tooltip, in display order
const TOOLTIP_CHIP_KEYS = ['type', 'stateId', 'importanceId', 'prioId', 'cat', 'updatedOn'];

/**
 * Builds the tooltip content for a single result item.
 * Reuses ContentHeaderField + CONTENT_HEADER_FIELD_DEFS — same chip styling/colors as EntryRow.
 * @param {object} item
 */
const ResultTooltipContent = ({ item }) => (
    <Box className="tgs-tooltip-content">
        <Typography sx={{ fontSize: '11px', color: 'var(--j-muted)', fontWeight: 600, mb: '4px' }}>
            {item.service}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', rowGap: '4px' }}>
            {TOOLTIP_CHIP_KEYS.map((key) => {
                const def = CONTENT_HEADER_FIELD_DEFS.find((d) => d.key === key);
                if (!def) return null;
                return <ContentHeaderField key={key} field={key} def={def} value={item[key]} />;
            })}
        </Box>
    </Box>
);

/**
 * Renders the search result grid.
 * Pure painter for layout — pin toggle calls topicGroupSearchEngine directly
 * (no onX mutation props, per Development.Patterns rk=e265293c).
 * pinFeedbackRk is local UI-only state — which row currently shows the "Pinned in X" toast.
 * @param {{ results: object[], loading: boolean, query: string }} props
 */
export default function TopicGroupSearchResults({ results, loading, query }) {
    const [pinFeedbackRk, setPinFeedbackRk] = useState(null);

    const copyRk = (rk) => {
        navigator.clipboard.writeText(rk);
    };

    const togglePin = (e, item) => {
        e.stopPropagation();
        topicGroupSearchEngine.togglePin(item);

        setPinFeedbackRk(item.rk);
        setTimeout(() => setPinFeedbackRk((current) => (current === item.rk ? null : current)), PIN_FEEDBACK_MS);
    };

    if (loading) {
        return <div className="tgs-results-empty">Searching...</div>;
    }

    if (query.length >= 3 && results.length === 0) {
        return <div className="tgs-results-empty">No results for "{query}"</div>;
    }

    if (results.length === 0) {
        return <div className="tgs-results-empty">Type at least 3 characters to search</div>;
    }

    return (
        <div className="tgs-results-grid">
            {results.map((item) => (
                <Tooltip
                    key={item.rk}
                    title={<ResultTooltipContent item={item} />}
                    followCursor
                    componentsProps={tooltipSxElevated}
                >
                    <div className="tgs-result-row">
                        <div className={`tgs-result-title${item.stateId === 1 ? ' tgs-result-title--completed' : ''}`}>
                            {item.title}
                        </div>

                        {pinFeedbackRk === item.rk && (
                            <span className="tgs-pin-feedback">
                                {item.isPinned ? `Pinned in ${item.service}` : 'Unpinned'}
                            </span>
                        )}

                        <button
                            className="tgs-pin-btn"
                            onClick={(e) => togglePin(e, item)}
                        >
                            {item.isPinned ? (
                                <PushPinIcon fontSize="small" />
                            ) : (
                                <PushPinOutlinedIcon fontSize="small" />
                            )}
                        </button>

                        <button
                            className="tgs-copy-btn"
                            title="Copy RK"
                            onClick={() => copyRk(item.rk)}
                        >
                            <ContentCopyIcon fontSize="small" />
                        </button>
                    </div>
                </Tooltip>
            ))}
        </div>
    );
}
